'use client'

import { useMemo } from 'react'

import { BlockHeading } from '@/components/BlockHeading'
import { Tagline } from '@/components/pro-blocks/landing-page/tagline'
import { PayloadRichText } from '@/components/PayloadRichText'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { FaqSectionBlock } from '@/payload-types'
import { hasRichText } from '@/utilities/hasRichText'

type FaqSectionBlockProps = FaqSectionBlock

type FaqCategory = NonNullable<FaqSectionBlock['categories']>[number]

type FaqItem = NonNullable<FaqCategory['items']>[number]

function categoryTabValue(category: FaqCategory, index: number) {
  return category.id ?? `category-${index}`
}

function itemValue(categoryKey: string, item: FaqItem, index: number) {
  return `${categoryKey}-${item.id ?? index}`
}

function FaqAccordion({
  categoryKey,
  items,
  defaultValue,
  ariaLabel,
}: {
  categoryKey: string
  items: FaqItem[]
  defaultValue?: string
  ariaLabel: string
}) {
  return (
    <Accordion aria-label={ariaLabel} collapsible defaultValue={defaultValue} type="single">
      {items.map((item, index) => (
        <AccordionItem
          key={item.id ?? `${categoryKey}-${item.question}-${index}`}
          value={itemValue(categoryKey, item, index)}
        >
          <AccordionTrigger className="font-sans text-left text-base font-medium text-balance text-foreground normal-case! tracking-normal!">
            {item.question}
          </AccordionTrigger>
          <AccordionContent className="text-muted-foreground text-sm [&_a]:rounded-sm [&_a]:outline-none">
            <PayloadRichText
              className="text-sm [&_a]:underline [&_p]:m-0 [&_p+p]:mt-2"
              data={item.answer}
            />
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

export function FaqSectionBlock(props: FaqSectionBlockProps) {
  const hasTagline = Boolean(props.tagline?.trim())
  const hasHeading = Boolean(props.heading?.trim())
  const hasDescription = hasRichText(props.description)
  const headingId = hasHeading ? 'faq-heading' : undefined

  const categories = useMemo(
    () =>
      (props.categories ?? []).filter(
        (category) => category.label?.trim() && (category.items?.length ?? 0) > 0,
      ),
    [props.categories],
  )

  const useTabs = categories.length > 1

  const firstAccordionDefault = useMemo(() => {
    const category = categories[0]
    const first = category?.items?.[0]
    if (!category || !first) return undefined
    return itemValue(categoryTabValue(category, 0), first, 0)
  }, [categories])

  const hasFaqContent = categories.length > 0

  if (!hasFaqContent && !hasTagline && !hasHeading && !hasDescription) return null

  return (
    <section aria-labelledby={headingId} className="bg-background">
      <div className="container-padding-x mx-auto flex max-w-2xl flex-col gap-10 md:gap-12">
        {(hasTagline || hasHeading || hasDescription) && (
          <div className="section-title-gap-lg flex flex-col items-center text-center">
            {hasTagline ? <Tagline>{props.tagline}</Tagline> : null}
            {hasHeading ? (
              <BlockHeading
                className="text-foreground text-balance"
                headingStyle={props.headingStyle}
                id={headingId}
              >
                {props.heading}
              </BlockHeading>
            ) : null}
            {hasDescription ? (
              <PayloadRichText
                className="text-muted-foreground m-0 whitespace-normal [&_a]:underline [&_p]:m-0 [&_p+p]:mt-2"
                data={props.description}
              />
            ) : null}
          </div>
        )}

        {useTabs ? (
          <Tabs
            className="flex w-full flex-col items-center"
            defaultValue={categoryTabValue(categories[0]!, 0)}
          >
            <TabsList className="mb-4 h-auto w-full flex-col justify-center gap-2 bg-transparent p-0 sm:mb-8 sm:w-auto sm:flex-row">
              {categories.map((category, index) => (
                <TabsTrigger
                  key={categoryTabValue(category, index)}
                  value={categoryTabValue(category, index)}
                  className="data-[state=active]:bg-brand-gold data-[state=active]:text-zinc-950 bg-muted text-muted-foreground hover:text-foreground w-full rounded-full border border-transparent px-6 py-2.5 text-base font-medium transition-all data-[state=active]:shadow-sm sm:w-auto"
                >
                  {category.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {categories.map((category, index) => {
              const tabValue = categoryTabValue(category, index)
              const defaultValue = index === 0 ? firstAccordionDefault : undefined

              return (
                <TabsContent
                  key={tabValue}
                  value={tabValue}
                  className="animate-in fade-in-50 slide-in-from-bottom-2 mt-0 w-full duration-300"
                >
                  <FaqAccordion
                    ariaLabel={`${category.label} FAQ items`}
                    categoryKey={tabValue}
                    defaultValue={defaultValue}
                    items={category.items ?? []}
                  />
                </TabsContent>
              )
            })}
          </Tabs>
        ) : (
          categories[0] && (
            <FaqAccordion
              ariaLabel={`${categories[0].label} FAQ items`}
              categoryKey={categoryTabValue(categories[0], 0)}
              defaultValue={firstAccordionDefault}
              items={categories[0].items ?? []}
            />
          )
        )}
      </div>
    </section>
  )
}
